import dynamic from 'next/dynamic';
import { SetStateAction, useEffect, useMemo } from 'react';
import Media from 'react-media';

import { ProductDetail, ProductsFilterItem } from '@api/catalog';

import ControlViewItems from '@components/ControlViewItems';
import { SortFilter } from '@components/ControlViewItems/types';
import ProductFilter from '@components/Filters';
import ProductCard from '@components/ProductCard';
import Loader from '@components/controls/Loader';
import Pagination from '@components/controls/Pagination';

import { VIEW_OPTIONS } from '@scripts/enums/products';
import { Layout, scale, useTheme } from '@scripts/gds';
import { fromKopecksToRouble, getItemForMetrics, metricsPushEvent } from '@scripts/helpers';
import { useMedia } from '@scripts/hooks';

import { columns, prepareFilters } from './helper';
import { tableHeadStyles, tableStyles } from './styles';

const Table = dynamic(() => import('@components/Table'));

export const colLayoutItemProductCard = (matches: boolean, activeView: string) =>
    activeView === VIEW_OPTIONS.DEFAULT || (activeView === VIEW_OPTIONS.SMALL_LINES && !matches)
        ? 1
        : { xxxl: 4, lg: 3, xs: 2 };

const ProductsList = ({
    productsListData,
    totalPages,
    filters,
    isFiltersLoading,
    breadcrumbsItems,
    sortFilter,
    setSortFilter,
    preExpandedFilters,
    setProductDetailsData,
    setActiveView,
    activeView,
}: {
    productsListData: ProductDetail[] | undefined;
    activeView: string;
    setActiveView: (arg: string) => void;
    totalPages?: number;
    filters?: ProductsFilterItem;
    isFiltersLoading?: boolean;
    breadcrumbsItems?: { name: string; link: string }[];
    sortFilter?: SortFilter;
    setSortFilter?: (val: SetStateAction<SortFilter>) => void;
    preExpandedFilters?: any;
    setProductDetailsData?: (arg: ProductDetail[]) => void;
}) => {
    const { md, xs } = useMedia();
    const { layout } = useTheme();

    const tableData = useMemo(() => productsListData || [], [productsListData]);
    const filterFields = useMemo(() => (filters ? prepareFilters(filters) : []), [filters]);

    useEffect(() => {
        if (!productsListData || !productsListData.length) return;

        metricsPushEvent({
            event: 'view_item_list',
            ecommerce: {
                items: productsListData.map(item => ({
                    ...getItemForMetrics(item),
                    quantity: 1,
                })),
            },
        });
    }, [productsListData]);

    if (isFiltersLoading) return <Loader />;

    return (
        <Layout cols={{ xxxl: 12, sm: 2 }} gap={scale(3)}>
            {sortFilter && setSortFilter && (
                <Layout.Item
                    col={{ xxxl: '3/13', sm: 2 }}
                    css={{
                        display: 'flex',
                        justifyContent: filters ? 'space-between' : 'flex-end',
                        alignItems: 'center',
                        width: '100%',

                        [md]: {
                            marginTop: scale(4),
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: scale(2),
                        },
                    }}
                >
                    <ControlViewItems
                        categories={breadcrumbsItems?.slice(1, breadcrumbsItems.length)}
                        filterFields={filterFields}
                        filter={sortFilter}
                        setFilter={setSortFilter}
                        activeView={activeView}
                        setActiveView={setActiveView}
                        priceRange={
                            filters && {
                                min: fromKopecksToRouble(filters.min_price),
                                max: fromKopecksToRouble(filters.max_price),
                            }
                        }
                        preExpandedFilters={preExpandedFilters}
                        setProductDetailsData={setProductDetailsData}
                    />
                </Layout.Item>
            )}

            {filters && !isFiltersLoading && (
                <Layout.Item
                    col={{ xxxl: 2 }}
                    css={{
                        [md]: {
                            display: 'none',
                        },
                    }}
                >
                    <ProductFilter
                        categories={breadcrumbsItems?.slice(1, breadcrumbsItems.length)}
                        filterFields={filterFields}
                        priceRange={{
                            min: fromKopecksToRouble(filters.min_price),
                            max: fromKopecksToRouble(filters.max_price),
                        }}
                        fieldsWithSearch={['brand_id']}
                        isLoading={false}
                        preExpandedFilters={preExpandedFilters}
                        setProductDetailsData={setProductDetailsData}
                    />
                </Layout.Item>
            )}
            <Layout.Item col={{ xxxl: filters ? 10 : 12, sm: 2 }}>
                <Layout
                    cols={{ xxxl: 4, lg: 3, xs: 2 }}
                    css={{
                        gridRowGap: scale(3),
                        gridColumnGap: scale(3),
                        [md]: { gridRowGap: scale(2), gridColumnGap: scale(2) },
                        [xs]: { gridColumnGap: scale(1) },
                    }}
                >
                    {productsListData && productsListData?.length > 0 ? (
                        <Media query={{ minWidth: layout?.breakpoints.md || 1024 }} defaultMatches={false}>
                            {matches =>
                                activeView === VIEW_OPTIONS.SMALL_LINES && matches ? (
                                    <Layout.Item col={{ xxxl: 4, lg: 3, sm: 2 }}>
                                        <Table
                                            css={[tableStyles, tableHeadStyles]}
                                            columns={columns}
                                            data={tableData}
                                        />
                                    </Layout.Item>
                                ) : (
                                    productsListData?.map(item => (
                                        <Layout.Item key={item.id} col={colLayoutItemProductCard(matches, activeView)}>
                                            <ProductCard
                                                productItem={item}
                                                activeView={
                                                    !matches && activeView === VIEW_OPTIONS.SMALL_LINES
                                                        ? VIEW_OPTIONS.DEFAULT
                                                        : activeView
                                                }
                                            />
                                        </Layout.Item>
                                    ))
                                )
                            }
                        </Media>
                    ) : (
                        <h2>Товары не найдены</h2>
                    )}

                    {!!totalPages && totalPages > 1 && (
                        <>
                            <Layout.Item col={{ xxxl: 4, lg: 3, xs: 2 }}>
                                <Pagination
                                    isVisibleShowMore
                                    pages={totalPages}
                                    baseNumberPages={6}
                                    setProductDetailsData={setProductDetailsData}
                                />
                            </Layout.Item>
                        </>
                    )}
                </Layout>
            </Layout.Item>
        </Layout>
    );
};

export default ProductsList;
